/**
 * Shared, reduced-motion-safe motion toolkit.
 * ============================================
 *
 * Thin `"use client"` wrappers over framer-motion (already a dependency) that
 * let any brand make a page feel alive WITHOUT layout shift, without hurting
 * LCP, and while fully respecting `prefers-reduced-motion`. No new deps, no
 * images, no per-route files.
 *
 * The app is static-export and brand/product PAGES are server components, so
 * import these inside a `"use client"` island (or wrap a server-rendered
 * section's children — these components are themselves client islands).
 *
 * ---------------------------------------------------------------------------
 * USAGE
 * ---------------------------------------------------------------------------
 *
 *   import { Reveal, Stagger, StaggerItem } from '@/components/shared/motion';
 *
 *   // Single block fades + rises in on scroll:
 *   <Reveal as="section" className="...">
 *     <h2>Why travelers pick us</h2>
 *   </Reveal>
 *
 *   // Stagger a list/grid of children:
 *   <Stagger as="ul" className="grid gap-4">
 *     {features.map((f) => (
 *       <StaggerItem as="li" key={f.id}>{f.title}</StaggerItem>
 *     ))}
 *   </Stagger>
 *
 *   // Low-level hook when you need custom markup:
 *   const [ref, inView] = useInViewOnce();
 *
 *   // Idle float (decorative blobs / cards):
 *   <Floating className="...">{icon}</Floating>
 *
 * ---------------------------------------------------------------------------
 * REDUCED-MOTION + NO-JS BEHAVIOR (important for SEO + a11y)
 * ---------------------------------------------------------------------------
 * - prefers-reduced-motion: every primitive calls framer-motion's
 *   `useReducedMotion()` and short-circuits to a plain element rendered fully
 *   visible with NO opacity/transform animation. Reduced-motion users get
 *   instant, complete content.
 * - No-JS / pre-hydration / hydration failure: Reveal/Stagger/StaggerItem gate
 *   the animation behind a post-mount flag. The server-rendered HTML and the
 *   first client render emit a PLAIN element with NO inline `opacity: 0` and NO
 *   transform. So content is always present and fully visible in static HTML
 *   and can NEVER be permanently stuck invisible — framer-motion only enhances
 *   it after mount. (Trade-off: in-view content may animate on first paint;
 *   don't wrap critical LCP hero copy.)
 * - No layout shift: animations use only `transform` (y) and `opacity`, never
 *   height/margin.
 * - `Floating` rides the existing `.animate-float` CSS keyframe, so its content
 *   is always rendered; reduced motion simply drops the class.
 */

export { Reveal, default as RevealDefault, type RevealProps } from './Reveal';
export {
  Stagger,
  StaggerItem,
  type StaggerProps,
  type StaggerItemProps,
} from './Stagger';
export { useInViewOnce, type UseInViewOnceOptions } from './useInViewOnce';
export { Floating, type FloatingProps } from './Floating';
